import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import { reaction } from 'mobx';
import { Classes } from '@blueprintjs/core';

import { Box, Button, Card } from 'core/components';
import { formConsumer, ValidationErrorsOrHelpText } from 'core/form';
import AgentSelectorFormBody from 'app/views/synthetics/components/agentSelector/AgentSelectorFormBody';

import { AGENT_CAPABILITIES, TASK_TYPES } from '@kentik/ui-shared/synthetics/constants';
import AgentSelectorDialog from './AgentSelectorDialog';
import AgentSelectorDisplay from './AgentSelectorDisplay';

/**
 * This needs to be used within a Form context, but can work as a standalone component (not Field wrapped).
 *
 * Notes:
 *  * It supports small prop.
 *  * Use Field wrapping to add a label.
 *  * Empty text will be visible only when required is false. Otherwise form validation error message will be shown instead.
 *  * Use required prop directly on this component when you want this behavior. Otherwise strange behavior. This is needed due to
 *    unfortunately-baked-into-skaterdav's-shitshow stuff like required_if.
 *
 * Example usage (Field wrapped, required, large):
 * <Field name="agents" label="Agents" rules="required" large>
 *   <AgentSelector required />
 * </Field>
 *
 * or (non-Field wrapped, not-required, small)
 *
 * <AgentSelector small />
 */
@formConsumer
@inject('$syn', '$labels')
@observer
class AgentSelector extends Component {
  static defaultProps = {
    autoSubmit: false,
    agentFamilyFilter: null,
    addButtonText: 'Add Agents',
    editButtonText: 'Edit Agents',
    showEditButton: true,
    emptyText: 'No agents selected',
    inlineForm: false, // show form inline if true, as dialog if false
    hideGlobalAgents: false,
    hidePrivateAgents: false,
    hideNetworkAgents: false,
    showPendingAgents: false,
    name: 'agents',
    readOnly: false,
    required: false,
    showErrors: false,
    small: false,
    large: false,
    multi: true,
    maxHeight: 300,
    overflow: 'auto'
  };

  state = {
    isOpen: false,
    agents: []
  };

  fieldDisposer;

  constructor(props) {
    super(props);

    const { $syn } = props;
    $syn.agents.fetch();
  }

  componentDidMount() {
    const { name: fieldName, form } = this.props;
    this.fieldDisposer = reaction(
      () => form.getValue(fieldName),
      () => this.setState({ agents: form.getValue(fieldName) || [] }),
      { fireImmediately: true }
    );
  }

  componentWillUnmount() {
    if (this.fieldDisposer) {
      this.fieldDisposer();
    }
  }

  get showError() {
    const { name: fieldName, form, showErrors } = this.props;
    const field = form.getField(fieldName);
    return showErrors && field.hasError && (field.showPristineErrors || !field.pristine);
  }

  handleSave = () => {
    const { autoSubmit, form, name: fieldName, onSubmit } = this.props;
    const { agents } = this.state;

    this.setState({ isOpen: false });
    form.setValue(fieldName, agents, { validate: false, pristine: false });

    form.validate();

    if (autoSubmit && form.dirty && form.validate()) {
      onSubmit();
    }
  };

  handleClose = () => {
    const { form, name: fieldName, onClose } = this.props;

    this.setState({
      isOpen: false,
      agents: form.getValue(fieldName) || []
    });

    if (onClose) {
      onClose();
    }
  };

  handleEdit = () => {
    const { form, name: fieldName } = this.props;
    this.setState({
      isOpen: true,
      agents: form.getValue(fieldName) || []
    });
  };

  handleChange = (value) => {
    const { inlineForm, form, autoSubmit, name: fieldName, onSubmit } = this.props;
    this.setState({ agents: value });
    if (inlineForm) {
      form.setValue(fieldName, value, { validate: false, pristine: false });
      form.validate();
      if (autoSubmit && form.dirty && form.validate()) {
        onSubmit();
      }
    }
  };

  getValuesWithOptionRemoved(option) {
    const { agents: agentId } = this.state;
    const agents = Array.isArray(agentId) ? agentId : [agentId]; // Single Agent Selector returns Id rather than array
    const index = agents.findIndex((id) => id === option.value);
    if (index !== -1) {
      return [...agents.slice(0, index), ...agents.slice(index + 1)];
    }
    return agents;
  }

  handleRemoveAndSubmit = (option) => {
    const { autoSubmit, name: fieldName, form, onSubmit } = this.props;
    const values = this.getValuesWithOptionRemoved(option);
    const field = form.getField(fieldName);
    field.setValue(values, { pristine: false });

    if (autoSubmit && form.dirty && form.validate()) {
      onSubmit();
    }
  };

  handleRemove = (option) => {
    this.setState({ agents: this.getValuesWithOptionRemoved(option) });
  };

  renderError() {
    const { name: fieldName, form, small } = this.props;
    const field = form.getField(fieldName);

    if (this.showError) {
      return (
        <Box className={Classes.FORM_GROUP} mb="4px" mt="4px">
          <ValidationErrorsOrHelpText field={field} small={small} />
        </Box>
      );
    }

    return null;
  }

  render() {
    const {
      $syn,
      $labels,
      agentFamilyFilter,
      addButtonText,
      editButtonText,
      showEditButton,
      emptyText,
      inlineForm,
      relationshipErrors,
      name: fieldName,
      hideGlobalAgents,
      hidePrivateAgents,
      // TODO: deprecate browserAgents #7660
      hideNetworkAgents,
      showPendingAgents,
      showIdColumn,
      form,
      disabled,
      maxHeight,
      readOnly,
      required,
      small,
      large,
      multi,
      overflow,
      model,
      overrideOptions,
      overrideSections,
      optionFilter = null
    } = this.props;
    const { isOpen, agents } = this.state;

    const field = form.getField(fieldName);

    // TODO: deprecate browserAgents #7660
    let options = [];
    if (hideGlobalAgents) {
      options = $syn.agents.privateAgentOptions;
    } else if (hideNetworkAgents) {
      options = $syn.agents.browserAgentOptions;
    } else {
      options = $syn.agents.agentOptions;
    }
    if (showPendingAgents) {
      options = options.concat($syn.agents.pendingAgentOptions);
    }

    if (overrideOptions) {
      options = overrideOptions;
    }

    if (optionFilter) {
      options = options.filter(optionFilter);
    }

    // AWS WL Agents are not supported for DNS tests
    // https://github.com/kentik/ui-app/issues/16261
    if (model?.isDns) {
      options = options.filter((o) => o.capabilities.includes(AGENT_CAPABILITIES.DNS));

      if (form.getValue('config.tasks').some((item) => item === TASK_TYPES.PING || item === TASK_TYPES.KNOCK)) {
        options = options.filter((o) => o.capabilities.includes(AGENT_CAPABILITIES.SYNGEST));
      }
    }

    if (inlineForm) {
      return (
        <div>
          <AgentSelectorFormBody
            agents={agents}
            onChange={this.handleChange}
            options={options}
            allOptions={options}
            hideGlobalAgents={hideGlobalAgents}
            hidePrivateAgents={hidePrivateAgents}
            hideNetworkAgents={hideNetworkAgents}
            showPendingAgents={showPendingAgents}
            showIdColumn={showIdColumn}
            small={small}
            large={large}
            multi={multi}
            overrideSections={overrideSections}
          />
          {this.renderError()}
        </div>
      );
    }

    return (
      <Box>
        <Card overflow={overflow} maxHeight={maxHeight} p={1}>
          <AgentSelectorDisplay
            disabled={disabled}
            fieldName={fieldName}
            relationshipErrors={relationshipErrors}
            onRemove={this.handleRemoveAndSubmit}
            options={options}
            readOnly={readOnly}
            emptyText={emptyText}
            showEmptyText={!this.showError}
            small={small}
            multi={multi}
          />
        </Card>
        {this.renderError()}
        {!readOnly && showEditButton && (
          <Button
            icon={agents.length ? 'edit' : 'add'}
            small={small}
            disabled={disabled}
            text={agents.length ? editButtonText : addButtonText}
            onClick={this.handleEdit}
            alignSelf="flex-start"
            mt={1}
          />
        )}
        {!readOnly && !disabled && isOpen && (
          <AgentSelectorDialog
            agents={agents}
            agentFamilyFilter={agentFamilyFilter}
            agentLabels={$labels.getLabels('synth_agent')}
            isOpen={isOpen}
            onCancel={this.handleClose}
            onChange={this.handleChange}
            onRemove={this.handleRemove}
            onSave={this.handleSave}
            options={options}
            pristine={field.pristine}
            required={required}
            hideGlobalAgents={hideGlobalAgents}
            hidePrivateAgents={hidePrivateAgents}
            hideNetworkAgents={hideNetworkAgents}
            showPendingAgents={showPendingAgents}
            showIdColumn={showIdColumn}
            fieldName={fieldName}
            multi={multi}
            overrideSections={overrideSections}
          />
        )}
      </Box>
    );
  }
}

export default AgentSelector;
